import { FC, useState, useEffect, Dispatch, SetStateAction } from 'react'
import Link from '@corratech/pylot-ui/src/Link'
import s from './Navbar.module.scss'
import { UserNav } from '@components/common'
import cn from 'classnames'
import throttle from 'lodash.throttle'
import { MegaMenu } from '@components/alfakher/pylot-megamenu'
import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import { Chat, Call } from '@components/icons'
import Logo from '@components/icons/Logo'
import { Button } from '@corratech/pylot-ui'

/**
 * Is used to query element by id and get navbar's height as an offset
 * when scrolling to the target element.
 */
export const NAVBAR_ID = 'navbar'
const SHOW_CURRENCY = false
const Searchbar = dynamic(() => import('@components/common/Searchbar'), {
    ssr: false
})

declare global {
    interface Window {
        $zoho: {
            salesiq: {
                floatwindow: {
                    visible: (isVisible: string) => void
                    isOpen: () => boolean
                    // Add other properties and methods as needed
                }
                ready: () => void
                floatbutton: {
                    visible: (action: 'hide' | 'show') => void
                }
            }
        }
    }
}

const Navbar: FC<{
    displaySearch: boolean
    setIsLoader?: Dispatch<SetStateAction<boolean>>
    setIsPromoCookieCleared?: Dispatch<SetStateAction<boolean>>
    isPromoCookieCleared?: boolean
}> = ({ displaySearch = false, setIsLoader = undefined }) => {
    const [hasScrolled, setHasScrolled] = useState(false)
    const { t } = useTranslation('common')

    useEffect(() => {
        const handleScroll = throttle(() => {
            const offset = 0
            const { scrollTop } = document.documentElement
            const scrolled = scrollTop > offset
            setHasScrolled(scrolled)
        }, 200)

        document.addEventListener('scroll', handleScroll)
        return () => {
            document.removeEventListener('scroll', handleScroll)
        }
    }, [])

    const renderLogoAndNavigation = () => (
        <Link href="/">
            <span className={s.logo} aria-label={t('Logo')}>
                <Logo />
                <span className="sr-only">{t('Logo')}</span>
            </span>
        </Link>
    )

    const openChat = () => {
        // Trigger Zoho Live Chat when the button is clicked
        if (typeof window !== 'undefined' && window.$zoho?.salesiq) {
            window.$zoho.salesiq.floatwindow.visible('show')
        }
    }

    return (
        <div
            className={cn(
                s.root,
                hasScrolled ? s['shadow-magical'] : s['fixed-search'],
                hasScrolled && 'nav-sticky'
            )}
        >
            <div className={s['header-main']}>
                <div className={s['top-header-section']}>
                    <div className={s['top-header-logo']}>
                        {renderLogoAndNavigation()}
                    </div>
                    <div className={s['top-header-search']}>
                        <div className={cn(s['desktop-search-display'])}>
                            <Searchbar />
                        </div>
                    </div>
                    <div className={s['top-header-contact-section']}>
                        <span className={s['chat-icon']}>
                            <Button variant="link" onClick={openChat}>
                                <Chat />
                                {t('Live Chat')}
                            </Button>
                        </span>
                        <span className={s['call-icon']}>
                            <Link href="/content/contact-us">
                                <Call />
                                {t('Contact Us')}
                            </Link>
                        </span>
                    </div>
                    <div className={s['top-header-right-section']}>
                        <UserNav showCurrency={SHOW_CURRENCY} />
                    </div>
                </div>
                <div id={NAVBAR_ID} className={s['nav-container']}>
                    <div className={s['nav-container-inner']}>
                        <div className={s['nav-section']}>
                            <MegaMenu
                                className={cn(
                                    'megamenu-cms',
                                    !hasScrolled && 'megamenu-not-sticky'
                                )}
                                showCurrency={SHOW_CURRENCY}
                                handleChatOpen={openChat}
                            />
                            <div
                                className={cn(
                                    s['mobile-search-display'],
                                    s['sticky-search']
                                )}
                            >
                                <Searchbar id="mobile-search-scroll" />
                            </div>
                            <div className={s['mobile-logo']}>
                                {renderLogoAndNavigation()}
                            </div>
                            <div className={s['usernav-mobile']}>
                                <span className={s['chat-icon']}>
                                    <Button variant="link" onClick={openChat}>
                                        <Chat />
                                    </Button>
                                </span>
                                <UserNav showCurrency={SHOW_CURRENCY} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Navbar
